import {
  type SizeFilterDepartment,
  type SizeFiltersResponse,
} from '@/modules/filters/aliases';

/**
 * Maps product group attribute values to size tree group names.
 * This handles cases where the API uses different naming conventions.
 */
export const PRODUCT_GROUP_TO_SIZE_TREE_NAME: Record<string, string> = {
  footwear: 'shoes',
  'coats-jackets': 'outerwear',
};

/**
 * Filters the size filter tree to only include categories (groups) that match
 * the current product's group.
 *
 * For example, if viewing a "tops" product, only show size filters for tops,
 * hiding bottoms, shoes, etc.
 *
 * @param sizeTree The full size filter tree
 * @param productGroup The current product's group (e.g., "tops", "bottoms", "footwear")
 * @returns Filtered size tree containing only matching groups
 */
export function filterSizeTreeByGroup(
  sizeTree: SizeFiltersResponse,
  productGroup?: string
): SizeFiltersResponse {
  // If no product group specified, return empty array to avoid showing all groups
  if (!productGroup) {
    return [];
  }

  // Normalize and map the product group to the size tree group name
  const normalizedProductGroup = productGroup.toLowerCase();
  const sizeTreeGroupName =
    PRODUCT_GROUP_TO_SIZE_TREE_NAME[normalizedProductGroup] ||
    normalizedProductGroup;

  // Filter each gender branch (e.g., Menswear, Womenswear)
  return sizeTree
    .map((genderBranch) => {
      // Filter the group/category level (e.g., Tops, Bottoms, Shoes)
      const filteredGroups = genderBranch.children.filter((groupBranch) => {
        if (!groupBranch.name) {
          return false;
        }
        return groupBranch.name.toLowerCase() === sizeTreeGroupName;
      });

      // If no matching groups, exclude this gender branch entirely
      if (filteredGroups.length === 0) {
        return null;
      }

      // Return gender branch with only matching groups
      return {
        ...genderBranch,
        children: filteredGroups,
      };
    })
    .filter((branch): branch is SizeFilterDepartment => branch !== null);
}
